import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import socket from "../socket";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = process.env.REACT_APP_API_BASE;

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmData, setConfirmData] = useState(null);

  const storedUser = JSON.parse(localStorage.getItem("user"));
  const userId = storedUser?._id || localStorage.getItem("customerId");

  /* ==========================================================
     📡 Fetch job + bids
  ========================================================== */
  const fetchJob = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/customers/jobs/jobs/${jobId}`);
      setJob(res.data.job || null);
      setBids(res.data.bids || []);
    } catch (err) {
      console.error("❌ Failed to fetch job details", err);
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     ⚡ Socket Real-Time Updates
  ========================================================== */
  useEffect(() => {
    if (!jobId) return;
    fetchJob();

    socket.emit("joinRoom", { jobId });
    console.log("✅ Joined job room:", jobId);

    socket.on("job:bidReceived", (data) => {
      if (String(data.jobId) === String(jobId)) {
        toast.success("✨ New bid received!");
        setBids((prev) => {
          const exists = prev.some((b) => b._id === data.bid?._id);
          return exists ? prev : [...prev, data.bid];
        });
      }
    });

    socket.on("job:update", (update) => {
      if (String(update.jobId) === String(jobId)) {
        toast(`⚡ Job updated: ${update.status}`);
        fetchJob();
      }
    });

    return () => {
      socket.emit("leaveRoom", { jobId });
      socket.off("job:bidReceived");
      socket.off("job:update");
    };
  }, [jobId]);

  /* ==========================================================
     ✅ Accept Bid Logic
  ========================================================== */
  const acceptBid = async (workerId, price) => {
    try {
      await axios.post(`${API_BASE}/api/customers/jobs/accept-bid`, { jobId, workerId });

      const selectedWorker = bids.find(
        (b) => String(b.workerId?._id || b.workerId) === String(workerId)
      );

      toast.success("✅ Bid accepted successfully!");

      navigate("/bid-accepted", {
        state: {
          price,
          workerName: selectedWorker?.workerName || "Worker",
          jobTitle: job.jobTitle,
          jobId: job._id,
          scheduledAt: job.scheduledAt || null,
          location:
            `${job.street}, ${job.city}, ${job.province} ${job.postalCode}` ||
            job.location ||
            "Not Provided",
          customerId: job.customerId?._id || job.customerId,
          assignedTo:
            selectedWorker?._id ||
            selectedWorker?.workerId ||
            job.assignedTo,
        },
      });
    } catch (err) {
      console.error("❌ Error accepting bid", err);
      toast.error("❌ Failed to accept bid. Please try again.");
    }
  };

  /* ==========================================================
     🎯 Trigger Confirm Modal
  ========================================================== */
  const handleAcceptClick = (workerId, price) => {
    setConfirmData({ workerId, price });
  };

  /* ==========================================================
     🔗 Navigation helpers
  ========================================================== */
  const openWorkerProfile = (workerId) => {
    if (!workerId) return;
    navigate(`/worker-profile/${workerId}?jobId=${job._id}&price=${job.budget}`);
  };

  const openChat = (workerId) => {
    if (!userId || !workerId || !jobId) {
      toast.error("Chat unavailable. Missing data.");
      return;
    }
    navigate(`/chat/${userId}/${workerId}/${jobId}`);
  };

  /* ==========================================================
     🖥️ UI Rendering
  ========================================================== */
  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <p className="text-xl font-bold text-gray-600 animate-pulse">
          Loading Job Details...
        </p>
      </div>
    );

  if (!job)
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <p className="text-xl font-bold text-red-500">Job Not Found!</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-6">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-blue-700 mb-2 drop-shadow-sm">
            Job Details
          </h1>
          <p className="text-gray-500">
            Ticket ID:{" "}
            <span className="text-blue-600 font-semibold">
              BYN-{job._id.slice(-4).toUpperCase()}
            </span>
          </p>
        </div>

        {job.status === "reopened" && (
          <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 rounded-xl mb-6 shadow-sm">
            🔄 This job was reopened after a worker cancellation. Review new bids below.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-10 text-gray-800">
          <div>
            <p><strong>Service:</strong> {job.jobTitle}</p>
            <p><strong>Budget:</strong> ${job.budget}</p>
            <p><strong>Status:</strong> {job.status}</p>
          </div>
          <div>
            <p><strong>Location:</strong> {job.location}</p>
            <p>
              <strong>Preferred Time:</strong>{" "}
              {job.scheduledAt
                ? new Date(job.scheduledAt).toLocaleString()
                : "ASAP"}
            </p>
            {job.description && (
              <p><strong>Description:</strong> {job.description}</p>
            )}
          </div>
        </div>

        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold text-gray-800 mb-6">Bids Received</h2>

        {bids.length === 0 ? (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-lg shadow-sm">
            No bids received yet. Please wait for workers to respond.
          </div>
        ) : (
          <div className="space-y-6">
            {bids.map((bid) => (
              <div
                key={bid._id}
                className="border border-gray-200 rounded-2xl p-6 bg-gradient-to-br from-white to-blue-50 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {bid.workerName}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Bid: <strong>${bid.price}</strong>
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                  <button
                    onClick={() => openWorkerProfile(bid.workerId)}
                    className="bg-gray-800 hover:bg-black text-white py-2 px-5 rounded-full text-sm font-semibold transition-all"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => openChat(bid.workerId)}
                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-5 rounded-full text-sm font-semibold transition-all"
                  >
                    Chat
                  </button>
                  <button
                    onClick={() => handleAcceptClick(bid.workerId, bid.price)}
                    className="bg-green-500 hover:bg-green-600 text-white py-2 px-5 rounded-full text-sm font-semibold transition-all"
                  >
                    Accept Bid
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

{/* 💎 Animated Confirmation Modal */}
<AnimatePresence>
  {confirmData && (
    <>
      {/* Blurred overlay */}
      <motion.div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={() => setConfirmData(null)}
      />

      {/* Centered Modal Card */}
      <motion.div
        className="fixed inset-0 z-[80] flex items-center justify-center px-4"
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        transition={{ duration: 0.35, ease: [0.25, 0.8, 0.5, 1] }}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
            Confirm Bid Acceptance
          </h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            Are you sure you want to accept this bid for{" "}
            <span className="font-semibold text-blue-600">
              ${confirmData.price}
            </span>
            ?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                acceptBid(confirmData.workerId, confirmData.price);
                setConfirmData(null);
              }}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-full font-semibold hover:scale-105 transition-transform"
            >
              Yes, Accept
            </button>
            <button
              onClick={() => setConfirmData(null)}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-full font-semibold hover:bg-gray-300 hover:scale-105 transition-transform"
            >
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </>
  )}
</AnimatePresence>
    </div>
  );
};

export default JobDetails;
